import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { APP_ROUTES } from '@/common/constants'
import { Errors } from '@/common/strings'
import { AccountType, Account } from '@/common/types'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import useAccountStore from '@/account/accountStore'
import useAccountSwitcher from '@/account/useAccountSwitcher'

interface UseAccountTypeSubmissionParams {
    selectedAccountType: AccountType | null
    createAccountSuccess: boolean
    createAccountData?: Account
    existingAccount: Account | null
    conflictAccountId: string | null
}

export function useAccountTypeSubmission(params: UseAccountTypeSubmissionParams) {
    const { selectedAccountType, createAccountSuccess, createAccountData, existingAccount, conflictAccountId } = params
    const { setAccounts, setPrimaryAccountId } = useAccountStore((s) => ({
        setAccounts: s.setAccounts,
        setPrimaryAccountId: s.setPrimaryAccountId,
    }))
    const switchAccount = useAccountSwitcher()
    const navigate = useNavigate()

    // Handle existing account reuse
    useEffect(() => {
        if (!selectedAccountType || !existingAccount?.accountId) return

        const { accountId } = existingAccount

        // Account already exists - just switch to it and navigate
        switchAccount(accountId).then((switched) => {
            if (!switched) {
                toast.error(Errors.server)
                return
            }

            trackAccountTypeSubmitted(selectedAccountType, accountId)
            navigate(APP_ROUTES.TERMS_AND_CONDITIONS)
        })
    }, [selectedAccountType, existingAccount, switchAccount, navigate])

    // Handle 409 conflict — user already has this account; switch to it and go to the dashboard.
    // switchAccount validates the target exists before resolving true, so a stale/invalid
    // conflictAccountId won't strand the user on a broken dashboard.
    useEffect(() => {
        if (!conflictAccountId) return

        switchAccount(conflictAccountId).then((switched) => {
            if (switched) {
                navigate(APP_ROUTES.DASHBOARD)
                return
            }

            toast.error(Errors.server)
        })
    }, [conflictAccountId, switchAccount, navigate])

    // Handle new account creation
    useEffect(() => {
        if (!selectedAccountType || !createAccountSuccess || !createAccountData?.accountId) return
        if (existingAccount) return // Skip if we're reusing existing account

        const { accountId, accountType } = createAccountData

        setAccounts([createAccountData])

        // Only INDIVIDUAL and ADVISER accounts can be primary
        if (accountType === AccountType.INDIVIDUAL || accountType === AccountType.ADVISER) {
            setPrimaryAccountId(accountId)
        }

        switchAccount(accountId).then((switched) => {
            if (!switched) {
                toast.error(Errors.server)
                return
            }

            trackAccountTypeSubmitted(selectedAccountType, accountId)
            navigate(APP_ROUTES.TERMS_AND_CONDITIONS)
        })
    }, [
        selectedAccountType,
        createAccountSuccess,
        createAccountData,
        existingAccount,
        setAccounts,
        switchAccount,
        navigate,
        setPrimaryAccountId,
    ])
}

function trackAccountTypeSubmitted(accountType: AccountType, accountId?: string) {
    analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.ONBOARDING_ACCOUNT_TYPE_SUBMITTED, {
        accountType,
        accountId,
    })
}
